import { Component, OnInit, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Casautomation } from 'src/app/services/casautomation.service';

interface HoldingRow {
  fund: string;
  category: string;
  value: string;
  percent: string;
}

interface AllocationRow {
  label: string;
  value: number;
  text: string;
  color: string;
}

interface ScoreCheck {
  name: string;
  score: number;
  verdict: string;
  badgeClass: string;
}

interface OverlapRow {
  level: string;
  percent: string;
  badgeClass: string;
}

@Component({
  selector: 'app-wealthLens-report-view',
  templateUrl: './wealthLens-report-view.component.html',
  styleUrls: ['./wealthLens-report-view.component.css']
})
export class WealthLensReportViewComponent implements OnInit {
  @ViewChild('reportPage', { static: false }) reportPage!: ElementRef;

  reportUser = {
    name: '',
    pan: '',
    rawPan: '',
    email: '',
    mobile: '',
    transactionId: '',
    reportType: '',
    rmLeadCode: '',
  };

  generatedOn = '';
  portfolioValue = '₹0';
  authToken = '';
  statusMessage = '';
  showStatus = false;
  isLoading = true;
  /** Shows the "No current investment found" popup when report data is missing. */
  noDataFound = false;
  private isLsqLeadSubmitted = false;
  private isLsqLeadSubmitting = false;
  private statusTimeout: any = null;

  // Fallback PAN for development
  private readonly fallbackPan = 'ALIPG1911D';

  // === PAGE 2: SCORE ===
  scoreValue = 6.9;
  scoreOutOf = 10;
  scoreVerdict = 'Good, But Scope for Improvement';
  scoreDescription = 'Your fund quality is strong and your risk alignment is close to on-target. The score is being held back almost entirely by one thing: too much overlap between your funds. Fixing that alone would move this score meaningfully higher.';

  scoreChecks: ScoreCheck[] = [
    { name: 'Diversification', score: 6.0, verdict: 'Needs Improvement', badgeClass: 'badge-red' },
    { name: 'Risk Alignment', score: 7.0, verdict: 'Good, But Scope for Improvement', badgeClass: 'badge-yellow' },
    { name: 'Quality of Funds', score: 7.7, verdict: 'Good, But Scope for Improvement', badgeClass: 'badge-yellow' },
  ];

  // === PAGE 3: HOLDINGS ===
  holdings: HoldingRow[] = [];

  // === PAGE 4: ASSET ALLOCATION ===
  allocations: AllocationRow[] = [];
  allocationInsight = '';

  // === PAGE 5: DIVERSIFICATION ===
  diversificationScore = 6.0; // out of 10, used for pointer position
  diversificationResult = '';
  diversificationCause = '';
  overlapBreakdown: OverlapRow[] = [
    { level: 'Good', percent: '60.5%', badgeClass: 'badge-green' },
    { level: 'Moderate', percent: '35.3%', badgeClass: 'badge-yellow' },
    { level: 'Poor', percent: '4.2%', badgeClass: 'badge-red' },
  ];

  // === PAGE 6: RISK + QUALITY ===
  riskEquityPercent = 74.8;
  riskAlignmentScore = 0;
  riskAlignmentResult = '';
  qualityScore = 7.7; // out of 10
  qualityResult = '';

  // === PAGE 7: NEXT STEPS ===
  nextStepSummary = '';

  // === RM DETAILS ===
  rmName = '';
  rmPhone = '';
  rmEmail = '';

  /** Show RM block only when name exists AND at least one of phone/email is present */
  get showRmDetails(): boolean {
    return !!this.rmName && (!!this.rmPhone || !!this.rmEmail);
  }

  // === RISK VALUES (based on selected reportType) ===
  selectedRiskLabel = 'Aggressive Risk';
  selectedRiskProfile = 'Aggressive';
  fundRiskValues: { fund: string; riskValue: number }[] = [];

  // === PDF EXPORT ===
  isExporting = false;
  /**
   * First export pass: renders every holding on one sheet so real row
   * heights can be measured, then the rows are re-chunked for the real pass.
   */
  pdfMeasuring = false;

  /** Breathing room (px) left at the bottom of a sheet after measuring. */
  private readonly pdfBodySafetyPx = 10;

  /** Fallback estimates, only used if measurement is unavailable. */
  private readonly pdfFirstHoldingsRows = 12;
  private readonly pdfNextHoldingsRows = 15;

  /**
   * Holdings split into PDF pages. Computed once when an export starts —
   * never expose this as a getter, or *ngFor would rebuild the whole
   * holdings DOM on every change-detection pass, mid-capture included.
   */
  pdfHoldingsPages: HoldingRow[][] = [];

  /**
   * Splits holdings across as many PDF pages as needed, always leaving room
   * for the TOTAL row on the final holdings page.
   */
  private buildHoldingsPages(): HoldingRow[][] {
    const rows = this.holdings || [];
    if (!rows.length) {
      return [[]];
    }

    const pages: HoldingRow[][] = [];
    let index = 0;
    let isFirst = true;

    while (index < rows.length) {
      const cap = isFirst ? this.pdfFirstHoldingsRows : this.pdfNextHoldingsRows;
      const remaining = rows.length - index;
      // +1 reserves a slot for the TOTAL row when this is the last page
      const take = remaining + 1 <= cap ? remaining : cap;
      pages.push(rows.slice(index, index + take));
      index += take;
      isFirst = false;
    }

    // No room left for the TOTAL row? Give it a page of its own...
    const lastCap = pages.length === 1 ? this.pdfFirstHoldingsRows : this.pdfNextHoldingsRows;
    if (pages[pages.length - 1].length + 1 > lastCap) {
      pages.push([]);
    }

    // ...but a page carrying nothing except the TOTAL row looks broken, so
    // pull one fund forward to keep it company.
    const last = pages[pages.length - 1];
    const previous = pages[pages.length - 2];
    if (!last.length && previous && previous.length > 1) {
      last.unshift(previous.pop() as HoldingRow);
    }

    return pages;
  }

  /**
   * Chunks holdings using the row heights actually measured in the DOM, so
   * long scheme names that wrap to two or three lines are accounted for
   * exactly instead of estimated. Falls back to buildHoldingsPages() if the
   * measurement pass didn't render as expected.
   */
  private measureHoldingsPages(): HoldingRow[][] {
    const rows = this.holdings || [];
    if (!rows.length) {
      return [[]];
    }

    const page = document.querySelector('#pdfRoot .pdf-holdings-page');
    const body = page?.querySelector('.pdf-body') as HTMLElement | null;
    const rowEls = Array.from(
      page?.querySelectorAll('tbody tr.pdf-holding-row') ?? []
    ) as HTMLElement[];

    // Bail out to estimates rather than risk a mis-chunked report.
    if (!page || !body || rowEls.length !== rows.length) {
      console.warn('WealthLens PDF: holdings measurement unavailable, using estimates');
      return this.buildHoldingsPages();
    }

    const heightOf = (selector: string): number =>
      (page.querySelector(selector) as HTMLElement | null)?.offsetHeight ?? 0;

    // .pdf-body is a flex child with min-height:0 inside a fixed-height sheet,
    // so clientHeight is the true space between header and footer even while
    // the measurement pass overflows it.
    const available = body.clientHeight - this.pdfBodySafetyPx;
    if (available <= 0) {
      console.warn('WealthLens PDF: sheet body has no measurable height, using estimates');
      return this.buildHoldingsPages();
    }

    const introHeight = heightOf('.pdf-holdings-intro');
    const continuedHeight = heightOf('.pdf-holdings-continued');
    const theadHeight = heightOf('thead');
    const totalRowHeight = heightOf('tbody tr.total-row');
    const rowHeights = rowEls.map((el) => el.offsetHeight);

    const pages: HoldingRow[][] = [];
    let current: HoldingRow[] = [];
    let used = introHeight + theadHeight;

    rows.forEach((row, i) => {
      // The final row has to share its sheet with the TOTAL row.
      const needed = rowHeights[i] + (i === rows.length - 1 ? totalRowHeight : 0);

      if (current.length && used + needed > available) {
        pages.push(current);
        current = [];
        used = continuedHeight + theadHeight;
      }

      current.push(row);
      used += rowHeights[i];
    });

    pages.push(current);
    return pages;
  }

  /** Page 1 = cover, page 2 = score, holdings start at page 3. */
  get pdfAllocationPage(): number {
    return 3 + this.pdfHoldingsPages.length;
  }

  get pdfDiversificationPage(): number {
    return this.pdfAllocationPage + 1;
  }

  get pdfRiskPage(): number {
    return this.pdfAllocationPage + 2;
  }

  get pdfQualityPage(): number {
    return this.pdfAllocationPage + 3;
  }

  get pdfNextStepsPage(): number {
    return this.pdfAllocationPage + 4;
  }

  // SVG gauge
  get gaugeCircumference(): number {
    return 2 * Math.PI * 60;
  }

  get gaugeStrokeDasharray(): string {
    const filled = (this.scoreValue / this.scoreOutOf) * this.gaugeCircumference;
    return `${filled} ${this.gaugeCircumference}`;
  }

  /**
   * Returns the score gauge as an inline SVG data-URI for the PDF export.
   * html2canvas renders <img> elements reliably but fails on inline SVG
   * stroke-dasharray, so we pre-bake the gauge into an image.
   */
  get pdfGaugeDataUri(): string {
    const circ = this.gaugeCircumference;
    const filled = (this.scoreValue / this.scoreOutOf) * circ;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 140 140" width="140" height="140">
      <circle cx="70" cy="70" r="60" fill="none" stroke="#eeeeee" stroke-width="10"/>
      <circle cx="70" cy="70" r="60" fill="none" stroke="#c11322" stroke-width="10"
        stroke-linecap="round" stroke-dasharray="${filled} ${circ}" stroke-dashoffset="0"
        transform="rotate(-90 70 70)"/>
    </svg>`;
    return 'data:image/svg+xml;base64,' + btoa(svg);
  }

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private casService: Casautomation,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.titleService.setTitle('Your WealthLens Report');
    this.metaService.updateTag({ name: 'robots', content: 'noindex, nofollow' });

    this.authToken = localStorage.getItem('wealthLensAuthToken') || '';
    const storedUser = localStorage.getItem('wealthLensReportUser');

    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        this.reportUser = { ...this.reportUser, ...parsed };
        if (!this.reportUser.pan && this.reportUser.rawPan) {
          this.reportUser.pan = this.maskPan(this.reportUser.rawPan);
          this.reportUser.rmLeadCode = this.reportUser.rmLeadCode;
        }
      } catch (error) {
        console.error('Unable to parse WealthLens report user', error);
      }
    }

    this.generatedOn = this.formatGeneratedDate();
    this.setDefaultInsights();
    this.fetchReportData();
  }

  fetchReportData(): void {
    this.isLoading = true;
    const panToUse = this.reportUser.rawPan || this.fallbackPan;
    const reportType = this.reportUser.reportType || 'AGGRESSIVE';
    const payload = { pan: panToUse, reportType };

    this.casService.getWealthLensReportData(payload, this.authToken).subscribe({
      next: (res: any) => {
        const hasData =
          res?.statusCode === 200 &&
          res?.data &&
          Array.isArray(res.data.fundDetails) &&
          res.data.fundDetails.length > 0;

        if (hasData) {
          this.mapApiResponse(res.data);
          // this.insertLSQLead(res.data);
        } else {
          // No holdings for this customer — show the popup instead of
          // rendering the report with placeholder values.
          this.noDataFound = true;
        }
        this.isLoading = false;
      },
      error: (err) => {
        // 404 / any failure means there is nothing to show.
        console.error('Failed to fetch WealthLens report data', err);
        this.noDataFound = true;
        this.isLoading = false;
      }
    });
  }

  /** "OK" on the no-data popup: send the user back to the report/form page. */
  goToReportPage(): void {
    this.router.navigate(['/wealth-report']);
  }

  private mapApiResponse(data: any): void {
    // The new API returns an object, not an array
    const fundDetails: any[] = data.fundDetails || [];

    // PAN from API if not from form
    if (!this.reportUser.pan && data.pan) {
      this.reportUser.pan = this.maskPan(data.pan);
    }
    if (!this.reportUser.rawPan && data.pan) {
      this.reportUser.rawPan = data.pan;
    }

    // Portfolio value from first fund detail
    if (fundDetails.length) {
      this.portfolioValue = this.formatCurrency(fundDetails[0]?.TOTAL_PORTFOLI_VALUE || 0);
    }

    // === WEALTHLENS SCORE (PAGE 2) ===
    this.scoreValue = data.WEALTHLENS_SCORE || 0;

    if (data.overallWealthLensScore) {
      this.scoreVerdict = data.overallWealthLensScore.assessment || '';
      this.scoreDescription = data.overallWealthLensScore.description || '';
    }

    // Score checks from individual sections
    this.scoreChecks = [];
    if (data.diversification) {
      this.scoreChecks.push({
        name: 'Diversification',
        score: data.diversification.score || 0,
        verdict: data.diversification.assessment || '',
        badgeClass: this.getBadgeClass(data.diversification.score)
      });
    }
    if (data.riskAlignment) {
      this.scoreChecks.push({
        name: 'Risk Alignment',
        score: data.riskAlignment.score || 0,
        verdict: data.riskAlignment.assessment || '',
        badgeClass: this.getBadgeClass(data.riskAlignment.score)
      });
    }
    if (data.qualityOfFunds) {
      this.scoreChecks.push({
        name: 'Quality of Funds',
        score: data.qualityOfFunds.score || 0,
        verdict: data.qualityOfFunds.assessment || '',
        badgeClass: this.getBadgeClass(data.qualityOfFunds.score)
      });
    }

    // === HOLDINGS (PAGE 3) ===
    this.holdings = fundDetails.map((item: any) => ({
      fund: item.SCHEME_NAME?.trim() || '',
      category: `${item.FUND_TYPE || ''}`,
      value: this.formatCurrency(parseFloat(parseFloat(item.CURRENTVALUE || '0').toFixed(2))),
      percent: `${(item.WEIGHT_PERCENTAGE || 0).toFixed(1)}%`
    }));

    // === ASSET ALLOCATION (PAGE 4) ===
    const allocationMap = new Map<string, number>();
    fundDetails.forEach((item: any) => {
      const fundType = item.FUND_TYPE || 'OTHER';
      const weight = fundType === "EQUITY" ? item.EQUITY_WEIGHT : fundType === "DEBT" ? item.DEBT_WEIGHT : fundType === "GOLD" ? item.GOLD_WEIGHT : item.OTHER_WEIGHT;
      allocationMap.set(fundType, weight);
    });

    const colorMap: { [key: string]: string } = {
      'Equity': 'green',
      'Debt': 'red',
      'Gold': 'orange',
      'Cash': 'yellow',
      'Other': 'grey',
      'Hybrid': 'yellow',
      'ELSS': 'blue',
    };

    this.allocations = Array.from(allocationMap.entries()).map(([label, value]) => ({
      label,
      value,
      text: `${value}%`,
      color: colorMap[label] || 'grey'
    }));

    // === DIVERSIFICATION (PAGE 5) ===
    if (data.diversification) {
      this.diversificationScore = data.diversification.score || 0;
      this.diversificationResult = data.diversification.description || '';
    }

    // Overlap breakdown from fund details
    if (fundDetails.length) {
      const first = fundDetails[0];
      this.overlapBreakdown = [
        { level: 'Good', percent: `${(first.GOOD_DIVERSIFICATION_PCT || 0).toFixed(1)}%`, badgeClass: 'badge-green' },
        { level: 'Moderate', percent: `${(first.MODERATE_DIVERSIFICATION_PCT || 0).toFixed(1)}%`, badgeClass: 'badge-yellow' },
        { level: 'Poor', percent: `${(first.POOR_DIVERSIFICATION_PCT || 0).toFixed(1)}%`, badgeClass: 'badge-red' },
      ];
    }

    // === RISK ALIGNMENT + QUALITY (PAGE 6) ===
    if (data.riskAlignment) {
      this.riskAlignmentResult = data.riskAlignment.description || '';
      this.riskAlignmentScore = data.riskAlignment.score || 0;
      // Use EQUITY_WEIGHT from fund details for the risk scale pointer
      if (fundDetails.length) {
        this.riskEquityPercent = fundDetails[0].EQUITY_WEIGHT || 0;
      }
    }

    if (data.qualityOfFunds) {
      this.qualityScore = data.qualityOfFunds.score || 0;
      this.qualityResult = data.qualityOfFunds.description || '';
    }

    // === RISK VALUE DISPLAY (show only the selected risk) ===
    // The reportType determines which risk column to show
    const reportType = data.reportType || this.reportUser.reportType || 'AGGRESSIVE';
    this.selectedRiskLabel = this.getRiskLabel(reportType);
    this.selectedRiskProfile = this.getRiskProfile(reportType);
    this.fundRiskValues = fundDetails.map((item: any) => {
      let riskValue = 0;
      if (reportType === 'AGGRESSIVE') riskValue = item.AGGRESSIVE_RISK || 0;
      else if (reportType === 'MODERATE') riskValue = item.MODERATE_RISK || 0;
      else if (reportType === 'DEFENSIVE') riskValue = item.DEFENSIVE_RISK || 0;
      return {
        fund: item.SCHEME_NAME?.trim() || '',
        riskValue
      };
    });

    // === NEXT STEPS (PAGE 7) ===
    // this.nextStepSummary = `Your WealthLens Score is ${this.scoreValue}/10. ` +
    //   (data.overallWealthLensScore?.description || '');
    this.nextStepSummary = 'Your WealthLens Score is the starting point. The real value lies in understanding what the individual insights mean for your portfolio. Review the areas highlighted in this report and connect with your Relationship Manager for a deeper discussion on what, if anything, may need attention.';

    // Allocation insight
    if (fundDetails.length) {
      const eq = fundDetails[0].EQUITY_WEIGHT || 0;
      const debt = fundDetails[0].DEBT_WEIGHT || 0;
      const gold = fundDetails[0].GOLD_WEIGHT || 0;
      this.allocationInsight = `Your portfolio is split across Equity (${eq}%), Debt (${debt}%), Gold (${gold}%), and Others. Consider whether this mix matches your risk appetite and time horizon.`;
    }

    // === RM DETAILS ===
    this.rmName = (data.RM_NAME || '').trim();
    this.rmEmail = (data.RM_EMAIL || '').trim();
    this.rmPhone = (data.RM_PHONE || '').trim();
  }

  private resolveAssignTo(rmEmail: string | undefined, rmLeadCode: string | undefined): string {
    const email = (rmEmail || '').trim();
    if (email) {
      return email;
    }
    // const fallback = (rmLeadCode || '').trim();
    // return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fallback) ? fallback : '';
    const fallback = (rmLeadCode || '').trim();
    return fallback;
  }

  /** Creates the WealthLens opportunity after the customer's report is available. */
  // insertLSQLead(reportData: any): void {
  //   if (this.isLsqLeadSubmitted || this.isLsqLeadSubmitting) {
  //     return;
  //   }

  //   this.isLsqLeadSubmitting = true;
  //   const fundDetails: any[] = reportData.fundDetails || [];
  //   const payload = {
  //     phone: this.reportUser.mobile || '',
  //     email: this.reportUser.email || '',
  //     firstName: this.reportUser.name || '',
  //     pan: this.reportUser.rawPan || reportData?.pan || '',
  //     campaignName: 'WealthLens',
  //     leadSource: 'RM',
  //     subLeadSource: this.resolveAssignTo(reportData.RM_EMAIL, this.reportUser.rmleadcode) ? 'RM' : (this.reportUser.rmleadcode || 'Website'),
  //     assign_to: this.resolveAssignTo(reportData.RM_EMAIL, this.reportUser.rmleadcode),
  //     portfolio_value_total: this.formatCurrency(fundDetails[0]?.TOTAL_PORTFOLI_VALUE || 0),
  //     portfolio_value_bajaj: this.formatCurrency(fundDetails[0]?.BAJAJ_PORTFOLI_VALUE || 0),
  //     portfolio_value_Nonbajaj: this.formatCurrency(fundDetails[0]?.NONBAJAJ_PORTFOLI_VALUE || 0),
  //     opportunityName: this.reportUser.name || 'Customer',
  //     Risk_Profile: this.selectedRiskProfile || this.reportUser.reportType || '',
  //     portfolio_date: new Date().toISOString().split('T')[0],
  //     wealthlens_score: String(reportData.WEALTHLENS_SCORE || 0),
  //   };
  //   this.casService.insertLSQLead(payload).subscribe({
  //     next: () => {
  //       this.isLsqLeadSubmitted = true;
  //       this.isLsqLeadSubmitting = false;
  //     },
  //     error: (error: unknown) => {
  //       this.isLsqLeadSubmitting = false;
  //       console.error('Failed to create WealthLens LSQ lead', error);
  //     }
  //   });
  // }

  private getBadgeClass(score: number): string {
    if (score >= 8) return 'badge-green';
    if (score >= 6.5) return 'badge-yellow';
    return 'badge-red';
  }

  private getRiskLabel(reportType: string): string {
    switch (reportType) {
      case 'AGGRESSIVE': return 'Aggressive Risk';
      case 'MODERATE': return 'Moderate Risk';
      case 'DEFENSIVE': return 'Defensive Risk';
      default: return 'Risk';
    }
  }

  private getRiskProfile(reportType: string): string {
    switch (reportType) {
      case 'AGGRESSIVE': return 'Aggressive';
      case 'MODERATE': return 'Moderate';
      case 'DEFENSIVE': return 'Defensive';
      default: return '';
    }
  }

  private setDefaultInsights(): void {
    this.diversificationResult = '';
    this.diversificationCause = '';
    this.riskAlignmentResult = '';
    this.qualityResult = '';
    this.allocationInsight = '';
    this.nextStepSummary = '';
  }

  // === PDF / SHARE ===
  async downloadPDF(): Promise<void> {
    if (this.isExporting) {
      return;
    }

    // Pass 1: every holding on a single sheet, purely to measure row heights.
    this.pdfMeasuring = true;
    this.pdfHoldingsPages = [this.holdings];
    this.isExporting = true;
    this.cdr.detectChanges();
    this.displayStatus('Preparing your PDF…', 60000);

    try {
      const html2pdf = await this.loadHtml2Pdf();

      await this.waitForPdfStage();

      // Pass 2: re-chunk against the measured heights and render for real.
      this.pdfHoldingsPages = this.measureHoldingsPages();
      this.pdfMeasuring = false;
      this.cdr.detectChanges();
      await this.waitForPdfStage();

      const element = document.getElementById('pdfRoot');
      if (!element) {
        throw new Error('PDF template not mounted');
      }

      // If the stage has no measurable box, html2pdf would emit a blank page.
      if (!element.offsetWidth || !element.offsetHeight) {
        throw new Error(
          `PDF stage has no layout (${element.offsetWidth}x${element.offsetHeight})`
        );
      }

      // html2canvas measures the target relative to the live document scroll.
      // The export stage is position:fixed and parked off-screen, so a page
      // that's been scrolled down shifts the captured content vertically and
      // the PDF comes out cropped / misaligned. Force the window to the top
      // for the duration of the capture, then restore the user's scroll.
      const savedScrollX = window.scrollX;
      const savedScrollY = window.scrollY;
      window.scrollTo(0, 0);

      try {
        await html2pdf()
          .set({
            margin: 0,
            filename: this.getFileName(),
            image: { type: 'jpeg', quality: 0.98 },
            // Keep these minimal: forcing width/windowWidth here truncates the canvas.
            html2canvas: {
              scale: this.pdfCanvasScale,
              useCORS: true,
              backgroundColor: '#ffffff',
              logging: false,
              // html2canvas defaults these to the current browser scroll offsets.
              // The export stage is fixed and deliberately positioned off-screen,
              // so inheriting a scrolled page moves the rendered PDF vertically.
              // Pin scroll to 0 and anchor capture to the element's own top-left
              // so the current page scroll position can't offset the output.
              scrollX: 0,
              scrollY: 0,
              x: 0,
              y: 0,
              windowWidth: element.scrollWidth,
              windowHeight: element.scrollHeight
            },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const },
            // Pagination is handled purely by geometry: each .pdf-page is exactly
            // one A4 slice tall, so they tile the output with zero drift. The
            // pagebreak plugin is deliberately off — it aligns breaks to a 1122px
            // grid while slices are 1122.5px, which drifts ~0.5px per page.
            pagebreak: { mode: [] }
          })
          .from(element)
          .save();
      } finally {
        // Put the user back exactly where they were before the export.
        window.scrollTo(savedScrollX, savedScrollY);
      }

      this.displayStatus('PDF downloaded ✓');
    } catch (err) {
      console.error('WealthLens PDF export failed', err);
      this.displayStatus('Could not generate the PDF. Please try again.', 4000);
    } finally {
      this.isExporting = false;
      this.pdfMeasuring = false;
      this.cdr.detectChanges();
    }
  }

  // downloadPDF(): void {
  //   this.displayStatus('Preparing your PDF…');

  //   this.loadHtml2Pdf().then((html2pdf) => {

  //     const originalElement = this.reportPage.nativeElement;

  //     // Clone so we don't modify the actual UI
  //     const element = originalElement.cloneNode(true) as HTMLElement;

  //     element.classList.add('pdf-export');

  //     // Put clone outside the visible page
  //     element.style.position = 'absolute';
  //     element.style.left = '-99999px';
  //     element.style.top = '0';
  //     element.style.width = '794px';
  //     element.style.maxWidth = '794px';
  //     element.style.minWidth = '794px';

  //     document.body.appendChild(element);

  //     const opt = {
  //       margin: 0,

  //       filename: this.getFileName(),

  //       image: {
  //         type: 'jpeg',
  //         quality: 0.98
  //       },

  //       html2canvas: {
  //         scale: 2,
  //         useCORS: true,
  //         allowTaint: true,

  //         scrollX: 0,
  //         scrollY: 0,

  //         width: 794,
  //         windowWidth: 794,

  //         backgroundColor: '#ffffff'
  //       },

  //       jsPDF: {
  //         unit: 'mm',
  //         format: 'a4',
  //         orientation: 'portrait'
  //       },

  //       pagebreak: {
  //         mode: ['css', 'legacy']
  //       }
  //     };

  //     html2pdf()
  //       .set(opt)
  //       .from(element)
  //       .save()
  //       .then(() => {
  //         document.body.removeChild(element);
  //         this.displayStatus('');
  //       })
  //       .catch((error: any) => {
  //         document.body.removeChild(element);
  //         console.error('PDF generation error:', error);
  //         this.displayStatus('');
  //       });

  //   });
  // }

  /**
   * Geometry contract with html2pdf — keep in sync with --pdf-page-w/h in the
   * stylesheet. html2pdf rasterises at 96 DPI and places the canvas at its
   * natural size, so an A4 sheet is 794 x 1122.5 px and nothing else.
   */
  readonly pdfPageWidth = 1000;
  private readonly pdfCanvasScale = 2;

  /**
   * Gives the browser a chance to lay out the PDF stage and settle webfonts
   * before html2canvas rasterises it.
   */
  private waitForPdfStage(): Promise<void> {
    const fontsReady: Promise<any> = (document as any).fonts?.ready
      ? (document as any).fonts.ready
      : Promise.resolve();

    return fontsReady.then(
      () =>
        new Promise<void>((resolve) => {
          requestAnimationFrame(() => setTimeout(() => resolve(), 300));
        })
    );
  }

  shareWhatsApp(): void {
    const msg = encodeURIComponent(
      `Hi, sharing the WealthLens Report for ${this.reportUser.name}. ` +
      `I've just downloaded the PDF (${this.getFileName()}) — attaching it here.`
    );
    const win = window.open(`https://wa.me/?text=${msg}`, '_blank', 'noopener');
    if (!win) {
      this.displayStatus('Pop-up blocked — allow pop-ups and try again', 4000);
      return;
    }
    this.displayStatus('Attach the downloaded PDF in WhatsApp');
    this.downloadPDF();
  }

  emailReport(): void {
    const subject = encodeURIComponent(`WealthLens Report — ${this.reportUser.name}`);
    const body = encodeURIComponent(
      `Hi,\n\nPlease find the WealthLens Report for ${this.reportUser.name} attached.\n\n` +
      `(The PDF has just been downloaded to your device as "${this.getFileName()}" — ` +
      `please attach it to this email before sending.)\n\nRegards,\n${this.reportUser.name}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    this.displayStatus('Attach the downloaded PDF in your email');
    this.downloadPDF();
  }

  // === HELPERS ===
  private getFileName(): string {
    const safeName = this.reportUser.name.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_');
    return `WealthLens_Report_${safeName || 'Customer'}.pdf`;
  }

  private loadHtml2Pdf(): Promise<any> {
    const existing = (window as any)['html2pdf'];
    if (existing) {
      return Promise.resolve(existing);
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
      script.integrity = 'sha512-GsLlZN/3F2ErC5ifS5QtgpiJtWd43JWSuIgh7mbzZ8zBps+dvLusV+eNQATqgA/HdeKFVgA5v3S/cIrLF7QnIg==';
      script.crossOrigin = 'anonymous';
      script.onload = () => {
        const lib = (window as any)['html2pdf'];
        lib ? resolve(lib) : reject(new Error('html2pdf not available'));
      };
      script.onerror = () => reject(new Error('Failed to load html2pdf script'));
      document.head.appendChild(script);
    });
  }

  private displayStatus(msg: string, duration = 2600): void {
    this.statusMessage = msg;
    this.showStatus = true;
    if (this.statusTimeout) clearTimeout(this.statusTimeout);
    this.statusTimeout = setTimeout(() => { this.showStatus = false; }, duration);
  }

  private formatGeneratedDate(): string {
    const now = new Date();
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return `${now.getDate()} ${months[now.getMonth()]} ${now.getFullYear()}`;
  }

  private formatCurrency(amount: number): string {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} L`;
    } else {
      return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
    }
  }

  private maskPan(pan: string): string {
    if (!pan || pan.length < 5) return pan || '';
    return `XXXXX${pan.slice(-5)}`;
  }

  /**
   * Clamps the scale-bar pointer position so the indicator circle
   * doesn't get cut off at 0% or 100% in the PDF export.
   * The pointer is ~18px wide with translate(-50%), so half sticks out
   * at extremes. Clamping to ~1.5%–98.5% keeps it fully visible.
   */
  clampPointerLeft(score: number): number {
    const raw = score * 10; // 0–100
    return Math.max(1.5, Math.min(98.5, raw));
  }
}
